import { TDeviceType } from '@cian/ui-kit';
import { equals } from 'ramda';

import { assertJsonQueryValidity } from './helpers/assertJsonQueryValidity';
import { trackNothingFound } from './tracking';
import { EFetchMapDataActionType, IFetchMapDataSucceedParameters } from './types';
import { resetTerms, TJsonQuery } from '../../../../packages/JsonQuery';
import { IJsonQuery } from '../../../../packages/api-models/common/json_query';
import { finishDrawing } from '../../../Map/actions';
import { trackPinLoadSuccessDebounced } from '../../../Map/tracking/trackPinLoadSuccess';
import { trackPrematurePinLoad } from '../../../Map/tracking/trackPrematurePinLoad';
import { createMapResponse200 } from '../../mappers/map/mapResponse200';
import { mapResponse400 } from '../../mappers/map/mapResponse400';
import { selectIsMultipinsExpActive } from '../../selectors/experiments';
import { fetchClusters as requestFetchClusters } from '../../services/clusters';
import { TThunkAction } from '../../types/redux';
import { isRequestCancelled } from '../../utils/isRequestCancelled';
import { isCenterZoomBounds, mapBBoxBoundsToBBox, queryStringWithMapBounds } from '../../utils/mapBounds';
import { actionGenerator } from '../../utils/redux/actionGenerator';
import { getVisibleTiles } from '../../utils/tiles';
import { closeOfferPreview } from '../closeOfferPreview/closeOfferPreview';
import { removeAllTags } from '../filters';
import { justChangeJsonQuery } from '../filters/justChangeJsonQuery';
import { EListingModalMode, EOffersFetchingMode, toggleModal } from '../listingModal';
import { removeOffers, setPage } from '../listingOffers';
import { searchWithNonGeoParameters } from '../nonGeoParameters';

const setStatusLoading = actionGenerator<EFetchMapDataActionType.Loading>(EFetchMapDataActionType.Loading);
const setStatusSucceed = actionGenerator<EFetchMapDataActionType.Succeed, IFetchMapDataSucceedParameters>(
  EFetchMapDataActionType.Succeed,
);
const setStatusFailed = actionGenerator<EFetchMapDataActionType.Failed>(EFetchMapDataActionType.Failed);
let loading: boolean;
let prevFetchStartTimestamp: number;
/**
 * Guard-флаг: пока запрос с updateBounds=true в полёте,
 * не-updateBounds вызовы пропускаются, чтобы cancellationTokenManager
 * не отменил запрос на получение новых bounds.
 *
 * Race condition: MF ре-рендер → resize контейнера Yandex Maps →
 * boundschange → MapFeaturesFetcher → fetchMapData(updateBounds:false) →
 * cancels нашего updateBounds:true запроса.
 */
// eslint-disable-next-line prefer-const
let pendingUpdateBoundsRequest = false;

function getPolygonCount(jsonQuery: TJsonQuery): number {
  const geoValue = (jsonQuery as IJsonQuery).geo?.value;

  if (!Array.isArray(geoValue)) {
    return 0;
  }

  return geoValue.filter(geo => geo.type === 'polygon').length;
}

export interface IFetchMapDataParameters {
  /**
   * Снимок применённого состояния карты (visibleBounds + zoom) на момент жеста.
   * Если задан — запрос строится из него (консистентная пара bounds/zoom), иначе из redux mapBounds.
   * Нужен, чтобы запрос не уходил на устаревшем redux-зуме: redux mapBounds обновляется
   * через троттл (useBoundsSync), а запрос инициируется мгновенно по actionend.
   * zoom влияет на запрос только при updateBounds=false (при true zoom не отправляется).
   */
  mapSnapshot?: { bounds: [YMaps.TCoord, YMaps.TCoord]; zoom: number };
  updateBounds?: boolean;
  deviceType: TDeviceType;
  requestJsonQuery?: TJsonQuery;
  allowNonGeoFallback?: boolean;
}

export function fetchMapData({
  mapSnapshot,
  updateBounds = false,
  deviceType,
  requestJsonQuery,
  allowNonGeoFallback = true,
}: IFetchMapDataParameters): TThunkAction<Promise<IFetchMapDataSucceedParameters | undefined>> {
  return async (dispatch, getState, context) => {
    if (pendingUpdateBoundsRequest && !updateBounds) {
      return undefined;
    }

    const {
      custom: { subdomain },
      logger,
    } = context;
    const {
      queryString,
      map: { mapBounds },
      filters: { jsonQuery: filtersJsonQuery },
      results: { jsonQuery: resultsJsonQuery },
    } = getState();
    const jsonQuery = requestJsonQuery || filtersJsonQuery;
    const isMultipinsEnabled = selectIsMultipinsExpActive(getState());

    const currentZoom = mapSnapshot?.zoom ?? mapBounds.zoom;
    const bounds = mapSnapshot?.bounds || mapBounds.bounds;

    const snapshotCenterZoom = mapSnapshot
      ? {
          center: [(bounds[0][0] + bounds[1][0]) / 2, (bounds[0][1] + bounds[1][1]) / 2] as YMaps.TCoord,
          zoom: currentZoom,
        }
      : null;

    const requestQueryString = snapshotCenterZoom
      ? queryStringWithMapBounds(queryString, snapshotCenterZoom)
      : queryString;

    if (!bounds || !currentZoom) {
      logger.error('Either bounds or zoom is undefined', { domain: 'actions/fetchMapData' });

      return undefined;
    }

    const clean = !equals(jsonQuery, resultsJsonQuery);

    const visibleTiles = getVisibleTiles(bounds, currentZoom);

    const currentFetchStartTimestamp = Date.now();
    // если предыдущий запрос не завершился, прошло более 2сек, и пользователь не дождавшись инициировал сл запрос,
    // то отправляем аналитическое событие о долгом запросе
    if (loading && currentFetchStartTimestamp - prevFetchStartTimestamp > 2000) {
      trackPrematurePinLoad({
        jsonQuery,
        deviceType,
        timeInMilliseconds: currentFetchStartTimestamp - prevFetchStartTimestamp,
        startTimestamp: currentFetchStartTimestamp,
        queryString,
      });
    }
    prevFetchStartTimestamp = currentFetchStartTimestamp;

    loading = true;
    if (updateBounds) {
      pendingUpdateBoundsRequest = true;
    }
    dispatch(setStatusLoading());

    try {
      const bbox = updateBounds ? undefined : mapBBoxBoundsToBBox({ bounds });
      const zoomToSend = updateBounds ? undefined : currentZoom;
      /*
       * отправляем queryString, чтобы после ответа бэка не терялись utm-метки из урлы
       * подробности в задаче https://jira.cian.tech/browse/CD-191441
       */
      const mapResponse = await requestFetchClusters(context, {
        subdomain,
        params: { zoom: zoomToSend, bbox, jsonQuery, queryString: requestQueryString },
        mappers: {
          200: createMapResponse200({ isMultipinsEnabled }),
          400: mapResponse400,
        },
      });

      // Если уже был сделан следующий запрос, то этот запрос останавливаем
      // AbortController внутри api service для этого недостаточно
      if (prevFetchStartTimestamp && prevFetchStartTimestamp > currentFetchStartTimestamp) {
        return undefined;
      }

      assertJsonQueryValidity(logger, jsonQuery, mapResponse.jsonQuery as unknown as IJsonQuery);

      // После ответа гео-смены (updateBounds) redux mapBounds «сырой» — bboxToMapBounds кладёт
      // только bounds, без center/zoom: дополнять queryString из него нельзя (упадёт на center).
      // Для запросов по снимку берём center/zoom снимка, иначе — mapBounds, если он полный.
      const responseCenterZoom = snapshotCenterZoom ?? (isCenterZoomBounds(mapBounds) ? mapBounds : null);

      const succeedPayload: IFetchMapDataSucceedParameters = {
        ...mapResponse,
        jsonQuery,
        queryString: responseCenterZoom
          ? queryStringWithMapBounds(mapResponse.queryString, responseCenterZoom)
          : mapResponse.queryString,
        clean,
        tiles: visibleTiles,
        updateBounds,
      };

      dispatch(setStatusSucceed(succeedPayload));

      trackPinLoadSuccessDebounced({
        jsonQuery,
        deviceType,
        timeInMilliseconds: Date.now() - currentFetchStartTimestamp,
        startTimestamp: currentFetchStartTimestamp,
        success: true,
        queryString,
      });

      const polygonCount = getPolygonCount(jsonQuery);
      const hasNonGeoOffers = Boolean(mapResponse.nonGeoOffersCount);
      const shouldUseNonGeoFallback =
        allowNonGeoFallback && !mapResponse.offersCount && hasNonGeoOffers && polygonCount === 0;

      if (shouldUseNonGeoFallback) {
        dispatch(finishDrawing());
        dispatch(removeAllTags());
        dispatch(searchWithNonGeoParameters({ deviceType }));
      } else if (!mapResponse.offersCount) {
        dispatch(removeOffers());
        dispatch(closeOfferPreview());
        dispatch(justChangeJsonQuery({ nextJsonQuery: resetTerms(['page'])(jsonQuery) }));
        dispatch(setPage(1));
        dispatch(
          toggleModal({
            open: true,
            mode: EListingModalMode.HALF_SCREEN,
            offersFetchingMode: EOffersFetchingMode.NONE,
          }),
        );
        trackNothingFound(deviceType);
      }

      return succeedPayload;
    } catch (error) {
      trackPinLoadSuccessDebounced({
        jsonQuery,
        deviceType,
        timeInMilliseconds: Date.now() - currentFetchStartTimestamp,
        startTimestamp: currentFetchStartTimestamp,
        success: false,
        queryString,
      });

      if (isRequestCancelled(error)) {
        return undefined;
      }

      logger.error(error);

      dispatch(removeOffers());
      dispatch(closeOfferPreview());
      dispatch(justChangeJsonQuery({ nextJsonQuery: resetTerms(['page'])(jsonQuery) }));
      dispatch(setPage(1));
      dispatch(
        toggleModal({
          open: true,
          mode: EListingModalMode.HALF_SCREEN,
          offersFetchingMode: EOffersFetchingMode.NONE,
        }),
      );
      trackNothingFound(deviceType);

      dispatch(setStatusFailed());

      return undefined;
    } finally {
      loading = false;
      if (updateBounds) {
        pendingUpdateBoundsRequest = false;
      }
    }
  };
}
